import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PieceDrawing } from "./PieceDrawing";
import { CORNER_KEYS, CORNER_LABELS, SIDES, SIDE_LABELS, THICKNESSES } from "../lib/pieces";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-mono text-[11px] uppercase tracking-wider text-[#9CA3AF]">{label}</Label>
    {children}
  </div>
);

const SideSelect = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="bg-[#0F1115] border-[#2D333F]"><SelectValue /></SelectTrigger>
    <SelectContent>
      {SIDES.map((s) => <SelectItem key={s} value={s}>{s} — {SIDE_LABELS[s]}</SelectItem>)}
    </SelectContent>
  </Select>
);

const PRESETS = [
  ["Todos", { C1: true, C2: true, L1: true, L2: true }],
  ["Comprimentos", { C1: true, C2: true, L1: false, L2: false }],
  ["Larguras", { C1: false, C2: false, L1: true, L2: true }],
  ["Nenhum", { C1: false, C2: false, L1: false, L2: false }],
];

export const MIN_SIZE = 15;
export const autoEdge = (mdf) => (mdf?.trim() ? `Fita ${mdf.trim()} 22mm` : "");

export const PieceDialog = ({ open, onOpenChange, piece, onSave }) => {
  const [p, setP] = useState(piece);
  useEffect(() => {
    setP(piece && {
      ...piece,
      led: { enabled: false, side: "C1", offset: 20, depth: 8, width: 10, ...(piece.led || {}) },
      backch: { enabled: false, side: "C2", offset: 12, depth: 8, width: 7, ...(piece.backch || {}) },
      radius: piece.radius || 0,
      corners: { C1L1: false, C1L2: false, C2L1: false, C2L2: false, ...(piece.corners || {}) },
      tampon: { enabled: false, type: "dupla", width: 50, thickness: 18, sides: { C1: true, C2: false, L1: false, L2: false }, ...(piece.tampon || {}) },
    });
  }, [piece]);
  if (!p) return null;
  const set = (k, v) => setP((o) => ({ ...o, [k]: v }));
  const setIn = (k, sub, v) => setP((o) => ({ ...o, [k]: { ...o[k], [sub]: v } }));
  const L = +p.length, W = +p.width;
  const fits = (L <= 2730 && W <= 1830) || (!p.grain && W <= 2730 && L <= 1830);
  const chanOk = (ch, w) => !ch.enabled || +ch.offset + w <= (ch.side.startsWith("C") ? W : L);
  const channelsOk = chanOk(p.groove, +p.groove.width || 6.6) && chanOk(p.led, +p.led.width || 0) && chanOk(p.backch, +p.backch.width || 0);
  const radiusOk = !(+p.radius > 0) || +p.radius <= Math.min(L, W) / 2;
  const roundOn = +p.radius > 0 || CORNER_KEYS.some((k) => p.corners[k]);
  const tooSmall = (L > 0 && L < MIN_SIZE) || (W > 0 && W < MIN_SIZE);
  const tamponOk = !p.tampon.enabled || p.tampon.type === "dupla" || (+p.tampon.width >= MIN_SIZE && +p.tampon.width <= 1000 && SIDES.some((s) => p.tampon.sides[s]));
  const valid = p.name.trim() && L > 0 && W > 0 && +p.qty >= 1 && fits && channelsOk && radiusOk && tamponOk;
  const num = (v) => (v === "" ? "" : Number(v));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="piece-dialog" className="max-w-4xl bg-[#181B21] border-[#2D333F] text-[#F3F4F6] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{piece?._isNew ? "Nova peça" : "Editar peça"}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-[1fr_300px] gap-6">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="Nome da peça">
                  <Input data-testid="piece-name-input" value={p.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex: Lateral" className="bg-[#0F1115] border-[#2D333F]" />
                </Field>
              </div>
              <Field label="Comprimento (mm)">
                <Input data-testid="piece-length-input" type="number" value={p.length} onChange={(e) => set("length", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
              </Field>
              <Field label="Largura (mm)">
                <Input data-testid="piece-width-input" type="number" value={p.width} onChange={(e) => set("width", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
              </Field>
              <Field label="Quantidade">
                <Input data-testid="piece-qty-input" type="number" min={1} value={p.qty} onChange={(e) => set("qty", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
              </Field>
              <Field label="Espessura do MDF">
                <Select value={String(p.thickness)} onValueChange={(v) => set("thickness", Number(v))}>
                  <SelectTrigger data-testid="piece-thickness-select" className="bg-[#0F1115] border-[#2D333F]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {THICKNESSES.map((t) => <SelectItem key={t} value={String(t)} data-testid={`thickness-option-${t}`}>{t} mm</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="col-span-2">
                <Field label="Cor / nome do MDF">
                  <Input data-testid="piece-mdf-color-input" value={p.mdf_color} onChange={(e) => {
                    const val = e.target.value;
                    setP((o) => ({ ...o, mdf_color: val, edge_color: !o.edge_color || o.edge_color === autoEdge(o.mdf_color) ? autoEdge(val) : o.edge_color }));
                  }} placeholder="Ex: Branco TX, Louro Freijó" className="bg-[#0F1115] border-[#2D333F]" />
                </Field>
              </div>
              <div className="col-span-2 flex items-center justify-between rounded-md border border-[#2D333F] px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Seguir veio do MDF</p>
                  <p className="text-xs text-[#6B7280]">A peça não será girada na chapa</p>
                </div>
                <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid="piece-grain-switch" checked={p.grain} onCheckedChange={(v) => set("grain", v)} />
              </div>
            </div>

            <div className="rounded-lg border border-[#2D333F] p-4 space-y-3">
              <p className="font-heading font-semibold">Furação para dobradiça</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quantidade de furos">
                  <Select value={String(p.hinge.qty)} onValueChange={(v) => setIn("hinge", "qty", Number(v))}>
                    <SelectTrigger data-testid="piece-hinge-qty-select" className="bg-[#0F1115] border-[#2D333F]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n === 0 ? "Sem furação" : `${n} dobradiça${n > 1 ? "s" : ""}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Em qual lado">
                  <SideSelect testId="piece-hinge-side-select" value={p.hinge.side} onChange={(v) => setIn("hinge", "side", v)} />
                </Field>
              </div>
              <p className="text-xs text-[#6B7280]">Furo Ø35 mm com centro a 22,5 mm da borda e 100 mm das pontas.</p>
            </div>

            <div className="rounded-lg border border-[#2D333F] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold">Canal para fundo de gaveta</p>
                <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid="piece-groove-switch" checked={p.groove.enabled} onCheckedChange={(v) => setIn("groove", "enabled", v)} />
              </div>
              {p.groove.enabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Field label="Lado"><SideSelect testId="piece-groove-side-select" value={p.groove.side} onChange={(v) => setIn("groove", "side", v)} /></Field>
                  <Field label="Largura">
                    <p data-testid="piece-groove-width-fixed" className="h-9 flex items-center font-mono text-sm text-[#D1D5DB]">6,6 mm</p>
                  </Field>
                  <Field label="Distância (mm)">
                    <Input data-testid="piece-groove-offset-input" type="number" value={p.groove.offset} onChange={(e) => setIn("groove", "offset", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                  <Field label="Profund. (mm)">
                    <Input data-testid="piece-groove-depth-input" type="number" value={p.groove.depth} onChange={(e) => setIn("groove", "depth", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                </div>
              )}
              <p className="text-xs text-[#6B7280]">Canal fixo de 6,6 mm feito com fresa de 6,5 mm, a 12 mm da base (padrão), começando 5 mm antes e terminando 5 mm depois da peça.</p>
            </div>

            <div className="rounded-lg border border-[#2D333F] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold">Canal para LED</p>
                <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid="piece-led-switch" checked={p.led.enabled} onCheckedChange={(v) => setIn("led", "enabled", v)} />
              </div>
              {p.led.enabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Field label="Lado"><SideSelect testId="piece-led-side-select" value={p.led.side} onChange={(v) => setIn("led", "side", v)} /></Field>
                  <Field label="Distância (mm)">
                    <Input data-testid="piece-led-offset-input" type="number" value={p.led.offset} onChange={(e) => setIn("led", "offset", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                  <Field label="Largura (mm)">
                    <Input data-testid="piece-led-width-input" type="number" value={p.led.width} onChange={(e) => setIn("led", "width", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                  <Field label="Profund. (mm)">
                    <Input data-testid="piece-led-depth-input" type="number" value={p.led.depth} onChange={(e) => setIn("led", "depth", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                </div>
              )}
              <p className="text-xs text-[#6B7280]">Mesmo padrão do canal de fundo: começa 5 mm antes e termina 5 mm depois da peça. Largura ajustável.</p>
            </div>

            <div className="rounded-lg border border-[#2D333F] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold">Canal para fundo</p>
                <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid="piece-backch-switch" checked={p.backch.enabled} onCheckedChange={(v) => setIn("backch", "enabled", v)} />
              </div>
              {p.backch.enabled && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Field label="Lado"><SideSelect testId="piece-backch-side-select" value={p.backch.side} onChange={(v) => setIn("backch", "side", v)} /></Field>
                  <Field label="Distância (mm)">
                    <Input data-testid="piece-backch-offset-input" type="number" value={p.backch.offset} onChange={(e) => setIn("backch", "offset", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                  <Field label="Largura (mm)">
                    <Input data-testid="piece-backch-width-input" type="number" value={p.backch.width} onChange={(e) => setIn("backch", "width", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                  <Field label="Profund. (mm)">
                    <Input data-testid="piece-backch-depth-input" type="number" value={p.backch.depth} onChange={(e) => setIn("backch", "depth", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                  </Field>
                </div>
              )}
              <p className="text-xs text-[#6B7280]">Canal para encaixar o fundo da caixa. Começa 5 mm antes e termina 5 mm depois da peça. Largura e profundidade ajustáveis.</p>
            </div>

            <div className="rounded-lg border border-[#2D333F] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold">Cantos arredondados</p>
                <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid="piece-round-switch" checked={roundOn}
                  onCheckedChange={(v) => setP((o) => ({
                    ...o,
                    radius: v ? (+o.radius > 0 ? o.radius : 50) : 0,
                    corners: v ? { C1L1: true, C1L2: true, C2L1: true, C2L2: true } : { C1L1: false, C1L2: false, C2L1: false, C2L2: false },
                  }))} />
              </div>
              {roundOn && (
              <div className="grid grid-cols-[120px_1fr] gap-3 items-end">
                <Field label="Raio (mm)">
                  <Input data-testid="piece-radius-input" type="number" min={0} value={p.radius} onChange={(e) => set("radius", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                </Field>
                <div className="flex flex-wrap gap-1.5">
                  {CORNER_KEYS.map((k) => (
                    <button key={k} type="button" data-testid={`corner-toggle-${k}`} onClick={() => setIn("corners", k, !p.corners[k])}
                      className={`rounded-md border px-2.5 py-1.5 font-mono text-xs transition-colors ${p.corners[k] ? "border-[#E67E22] bg-[#E67E22]/15" : "border-[#2D333F] text-[#9CA3AF]"}`}>
                      {CORNER_LABELS[k]}
                    </button>
                  ))}
                </div>
              </div>
              )}
              <p className="text-xs text-[#6B7280]">Escolha os cantos (encontro de dois lados). O DXF sai com o arco do raio.</p>
            </div>

            <div className="rounded-lg border border-[#2D333F] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-heading font-semibold">Tamponamento</p>
                <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid="piece-tampon-switch" checked={p.tampon.enabled} onCheckedChange={(v) => setIn("tampon", "enabled", v)} />
              </div>
              {p.tampon.enabled && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    {[["dupla", "Peça dupla", "Uma peça igual colada atrás"], ["regua", "Régua", "Tiras coladas nas bordas"]].map(([v, l, d]) => (
                      <button key={v} type="button" data-testid={`tampon-type-${v}`} onClick={() => setIn("tampon", "type", v)}
                        className={`text-left rounded-md border px-3 py-2 transition-colors ${p.tampon.type === v ? "border-[#E67E22] bg-[#E67E22]/15" : "border-[#2D333F] text-[#9CA3AF]"}`}>
                        <span className="block text-sm font-semibold">{l}</span><span className="text-[11px] opacity-70">{d}</span>
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Espessura do MDF">
                      <Select value={String(p.tampon.thickness)} onValueChange={(v) => setIn("tampon", "thickness", Number(v))}>
                        <SelectTrigger data-testid="tampon-thickness-select" className="bg-[#0F1115] border-[#2D333F]"><SelectValue /></SelectTrigger>
                        <SelectContent>{THICKNESSES.map((t) => <SelectItem key={t} value={String(t)}>{t} mm</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    {p.tampon.type === "regua" && (
                      <Field label="Largura da régua (mm)">
                        <Input data-testid="tampon-width-input" type="number" value={p.tampon.width} onChange={(e) => setIn("tampon", "width", num(e.target.value))} className="bg-[#0F1115] border-[#2D333F] font-mono" />
                      </Field>
                    )}
                  </div>
                  {p.tampon.type === "regua" && (
                    <div className="flex flex-wrap gap-1.5">
                      {SIDES.map((s) => (
                        <button key={s} type="button" data-testid={`tampon-side-${s}`} onClick={() => setIn("tampon", "sides", { ...p.tampon.sides, [s]: !p.tampon.sides[s] })}
                          className={`rounded-md border px-2.5 py-1.5 font-mono text-xs ${p.tampon.sides[s] ? "border-[#E67E22] bg-[#E67E22]/15" : "border-[#2D333F] text-[#9CA3AF]"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-[#6B7280]">As peças de tamponamento entram sozinhas no plano de corte e nas etiquetas, na espessura escolhida.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-[#2D333F] bg-[#0F1115] p-3 blueprint-grid">
              <PieceDrawing piece={p} onToggleEdge={(k) => setIn("edges", k, !p.edges[k])} className="w-full h-56" testId="piece-dialog-drawing" />
              <p className="text-[11px] text-center text-[#6B7280] mt-1">Clique nas bordas do desenho para marcar a fita</p>
            </div>
            <div className="space-y-2">
              <p className="font-heading font-semibold">Fita de borda</p>
              <div className="grid grid-cols-2 gap-2">
                {SIDES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`edge-checkbox-${s}`}
                    onClick={() => setIn("edges", s, !p.edges[s])}
                    className={`text-left rounded-md border px-2.5 py-2 text-xs transition-colors ${p.edges[s] ? "border-[#E67E22] bg-[#E67E22]/15 text-[#F3F4F6]" : "border-[#2D333F] text-[#9CA3AF] hover:border-[#4B5563]"}`}
                  >
                    <span className="font-mono font-bold mr-1">{s}</span>{SIDE_LABELS[s]}
                    <span className="block font-mono text-[10px] opacity-70">{s.startsWith("C") ? p.length : p.width} mm</span>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map(([n, v]) => (
                  <button key={n} type="button" data-testid={`edge-preset-${n.toLowerCase()}`} onClick={() => set("edges", v)}
                    className="text-[11px] rounded-full border border-[#2D333F] px-2.5 py-1 text-[#9CA3AF] hover:text-white hover:border-[#E67E22] transition-colors">
                    {n}
                  </button>
                ))}
              </div>
              <Field label="Cor / nome da fita de borda">
                <Input data-testid="piece-edge-color-input" value={p.edge_color} onChange={(e) => set("edge_color", e.target.value)} placeholder="Ex: Fita Freijó 22mm" className="bg-[#0F1115] border-[#2D333F]" />
              </Field>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-[#6B7280]">Muda sozinha com a cor do MDF. Você pode escrever outra.</p>
                <button type="button" data-testid="edge-match-mdf-btn" disabled={!p.mdf_color.trim()} onClick={() => set("edge_color", autoEdge(p.mdf_color))}
                  className="shrink-0 text-[11px] rounded-full border border-[#2D333F] px-2.5 py-1 text-[#9CA3AF] hover:text-white hover:border-[#E67E22] transition-colors disabled:opacity-40">
                  Igual ao MDF
                </button>
              </div>
            </div>
          </div>
        </div>
        {L > 0 && W > 0 && !fits && (
          <p data-testid="piece-size-error" className="text-sm text-red-400">
            A peça não cabe na área útil da chapa (2730 x 1830 mm{p.grain ? ", sem girar por causa do veio" : ""}).
          </p>
        )}
        {tooSmall && (
          <p data-testid="piece-min-size-error" className="text-sm text-red-400">
            Peças com menos de {MIN_SIZE} mm de comprimento ou largura não podem ser cortadas.
          </p>
        )}
        {!radiusOk && (
          <p data-testid="piece-radius-error" className="text-sm text-red-400">
            O raio não pode ser maior que a metade da menor medida da peça ({Math.min(L, W) / 2} mm).
          </p>
        )}
        {!channelsOk && (
          <p data-testid="piece-channel-error" className="text-sm text-red-400">
            Um canal ficou fora da peça: a distância mais a largura do canal passa da medida da peça.
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" data-testid="piece-dialog-cancel" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="piece-dialog-save" disabled={!valid} onClick={() => {
            if (tooSmall) {
              toast.error(`Peça muito pequena: comprimento e largura precisam ter pelo menos ${MIN_SIZE} mm.`);
              return;
            }
            onSave(p);
          }} className="bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">Salvar peça</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
